/**
 * Signup.jsx — Updated with OTP + admin approval flow
 *
 * Flow:
 * 1. User fills name, company, email, password
 * 2. Supabase creates auth user + sends OTP email
 * 3. Registration saved to user_registrations as "pending"
 * 4. User redirected to /verify-otp
 * 5. After OTP → /pending-approval
 * 6. Admin approves → user can access dashboard
 */

import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";
import { supabase } from "../supabaseClient";
import { adminOp } from "../adminApi";

export default function Signup() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    fullName:    "",
    companyName: "",
    email:       "",
    password:    "",
    confirmPassword: "",
  });
  const [showPassword,  setShowPassword]  = useState(false);
  const [showConfirm,   setShowConfirm]   = useState(false);
  const [loading,       setLoading]       = useState(false);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.fullName.trim())    { toast.error("Please enter your full name.");    return; }
    if (!form.companyName.trim()) { toast.error("Please enter your company name."); return; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirmPassword) { toast.error("Passwords do not match."); return; }

    setLoading(true);
    try {
      // 1. Create Supabase auth user — this sends OTP email automatically
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email:    form.email,
        password: form.password,
        options: {
          data: {
            full_name:    form.fullName,
            company_name: form.companyName,
          },
          emailRedirectTo: undefined, // we handle OTP manually
        },
      });

      if (authError) throw authError;

      


// 2. Check if company already exists
const { data: companyMatch } = await supabase
  .rpc('lookup_company_by_name', {
    p_company_name: form.companyName.trim()
  });

const existingCompany = companyMatch?.[0] || null;

// 3. Save registration as pending
const { error: regError } = await supabase
  .from("user_registrations")
  .insert({
    user_id:      authData.user?.id,
    email:        form.email,
    full_name:    form.fullName,
    company_name: form.companyName,
    company_id:   existingCompany?.company_id || null,
    status:       "pending",
    otp_verified: false,
  });

      if (regError) console.warn("Registration insert error:", regError);

      // 3. Redirect to OTP verification
      toast.success("Check your email for a verification code!");
      navigate("/verify-otp", { state: { email: form.email } });

    } catch (err) {
      toast.error(err.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#0a0b0f", display: "flex", fontFamily: "sans-serif" }}>

      {/* Left — Form */}
      <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 32 }}>
        <div style={{ width: "100%", maxWidth: 440 }}>

          <Link to="/" style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "#64748b", fontSize: 13, marginBottom: 28, textDecoration: "none" }}>
            <ArrowLeft size={15} /> Back to Home
          </Link>

          {/* Brand */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 28 }}>
            <div style={{
              width: 44, height: 44, borderRadius: 11,
              background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, fontWeight: 900, color: "#fff",
            }}>N</div>
            <div>
              <h1 style={{ fontSize: 20, fontWeight: 800, color: "#f1f5f9", margin: 0 }}>Create Account</h1>
              <p style={{ fontSize: 12, color: "#64748b", margin: 0 }}>Join Nebula Finance AI</p>
            </div>
          </div>

          {/* Steps indicator */}
          <div style={{ display: "flex", gap: 6, marginBottom: 28 }}>
            {[
              { n: 1, label: "Sign Up",  active: true  },
              { n: 2, label: "Verify",   active: false },
              { n: 3, label: "Approval", active: false },
            ].map((step) => (
              <div key={step.n} style={{ flex: 1, textAlign: "center" }}>
                <div style={{
                  width: 28, height: 28, borderRadius: "50%", margin: "0 auto 4px",
                  background: step.active ? "#6366f1" : "#1a1d26",
                  border: `1px solid ${step.active ? "#6366f1" : "#2a2d3a"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 11, fontWeight: 700,
                  color: step.active ? "#fff" : "#374151",
                }}>{step.n}</div>
                <div style={{ fontSize: 9, color: step.active ? "#818cf8" : "#374151", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  {step.label}
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Full Name */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Full Name *
              </label>
              <input
                id="fullName" name="fullName"
                value={form.fullName}
                onChange={(e) => handleChange("fullName", e.target.value)}
                placeholder="John Smith"
                required
                style={inputSt}
              />
            </div>

            {/* Company Name */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Company Name *
              </label>
              <input
                id="companyName" name="companyName"
                value={form.companyName}
                onChange={(e) => handleChange("companyName", e.target.value)}
                placeholder="Acme Corp Ltd"
                required
                style={inputSt}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Work Email *
              </label>
              <input
                id="email" name="email" type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="you@company.com"
                required
                style={inputSt}
              />
            </div>

            {/* Password */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Password *
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="password" name="password"
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => handleChange("password", e.target.value)}
                  placeholder="Min 6 characters"
                  required
                  style={{ ...inputSt, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={eyeBtnSt}>
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label style={{ display: "block", fontSize: 12, fontWeight: 600, color: "#94a3b8", marginBottom: 6 }}>
                Confirm Password *
              </label>
              <div style={{ position: "relative" }}>
                <input
                  id="confirmPassword" name="confirmPassword"
                  type={showConfirm ? "text" : "password"}
                  value={form.confirmPassword}
                  onChange={(e) => handleChange("confirmPassword", e.target.value)}
                  placeholder="Re-enter password"
                  required
                  style={{ ...inputSt, paddingRight: 44 }}
                />
                <button type="button" onClick={() => setShowConfirm(!showConfirm)} style={eyeBtnSt}>
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Notice */}
            <div style={{
              background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.2)",
              borderRadius: 8, padding: "10px 14px", fontSize: 11, color: "#818cf8", lineHeight: 1.6,
            }}>
              ℹ️ After verifying your email, your account will be reviewed by our admin team. You'll be notified once approved.
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: "12px", borderRadius: 10, border: "none",
                background: loading ? "#374151" : "#6366f1",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: loading ? "not-allowed" : "pointer", marginTop: 4,
              }}
            >
              {loading ? "Creating account…" : "Create Account →"}
            </button>
          </form>

          <p style={{ textAlign: "center", fontSize: 12, color: "#64748b", marginTop: 20 }}>
            Already have an account?{" "}
            <Link to="/login" style={{ color: "#818cf8", fontWeight: 600, textDecoration: "none" }}>Sign In</Link>
          </p>
        </div>
      </div>

      {/* Right — Visual */}
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        background: "linear-gradient(135deg, rgba(99,102,241,0.1), rgba(139,92,246,0.1))",
        borderLeft: "1px solid #2a2d3a", padding: 40,
      }} className="hidden lg:flex">
        <div style={{ textAlign: "center" }}>
          <div style={{
            width: 100, height: 100, borderRadius: 24, margin: "0 auto 24px",
            background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 48, fontWeight: 900, color: "#fff",
          }}>N</div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: "#f1f5f9", margin: "0 0 12px" }}>
            Nebula Finance AI
          </h2>
          <p style={{ fontSize: 13, color: "#64748b", maxWidth: 280, lineHeight: 1.7, margin: "0 auto 28px" }}>
            Real-time FX rates, treasury insights, and comprehensive financial tools for modern corporates.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 10, textAlign: "left" }}>
            {["AI-powered treasury insights", "Real-time FX exposure tracking", "Hedge position management", "Daily treasury email briefs"].map((f) => (
              <div key={f} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: "#4ade80", fontSize: 14 }}>✓</span>
                <span style={{ fontSize: 12, color: "#94a3b8" }}>{f}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

const inputSt = {
  width: "100%", padding: "10px 14px", borderRadius: 9,
  border: "1px solid #2a2d3a", background: "#1a1d26",
  color: "#f1f5f9", fontSize: 13, outline: "none",
  boxSizing: "border-box",
};

const eyeBtnSt = {
  position: "absolute", right: 12, top: "50%",
  transform: "translateY(-50%)",
  background: "none", border: "none",
  cursor: "pointer", color: "#64748b",
  display: "flex", alignItems: "center",
};
